import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAgency } from '../context/AgencyContext';
import { Button } from '../components/ui/button';
import CrossAgencyDashboard from '../components/CrossAgencyDashboard';
import TrendCharts from '../components/TrendCharts';
import RealTimeIndicator from '../components/RealTimeIndicator';
import { openElevationDialog } from '../components/PlatformElevation';
import { ActionRequiredInbox } from '../components/dashboard/ActionRequiredInbox';
import { HourglassSimple, FilePlus, ChartLineUp, ShieldCheck } from '@phosphor-icons/react';
import { useDashboardData } from './dashboard/useDashboardData';
import { PrimaryStatsGrid, SecondaryStatsGrid } from './dashboard/DashboardStats';
import InlineFilteredResults from './dashboard/InlineFilteredResults';
import { QuickActions, RecentClaimsCard } from './dashboard/QuickActionsAndRecent';
import { PlatformSummaryWidget } from './dashboard/PlatformSummaryWidget';
import { ManualReviewWidget } from '../components/dashboard/ManualReviewWidget';

const PendingApprovalBanner = () => (
  <div
    className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start gap-3"
    data-testid="pending-approval-banner"
  >
    <HourglassSimple size={24} className="text-amber-600 shrink-0 mt-0.5" weight="duotone" />
    <div>
      <h3 className="text-sm font-semibold text-amber-900">Agency Pending Approval</h3>
      <p className="text-xs text-amber-700 mt-0.5">
        Your agency registration is being reviewed by AZAI. You can start setting up your
        data while you wait. Claims submission and EVV reporting will be enabled after
        approval.
      </p>
    </div>
  </div>
);

const ElevationRequiredBanner = ({ agency }) => {
  const requestElevation = () => {
    if (agency?.id) openElevationDialog(agency.id, agency.name);
  };
  return (
    <div
      className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex items-start gap-3"
      data-testid="elevation-required-banner"
    >
      <ShieldCheck size={24} className="text-indigo-700 shrink-0 mt-0.5" weight="duotone" />
      <div className="flex-1">
        <h3 className="text-sm font-semibold text-indigo-900">
          Platform elevation required
        </h3>
        <p className="text-xs text-indigo-700 mt-0.5">
          You're signed in as AZAI platform staff. To see live billing data for{' '}
          <strong>{agency?.name || 'the selected agency'}</strong>, request a
          time-limited platform elevation. This is audited and expires automatically.
        </p>
      </div>
      <Button
        onClick={requestElevation}
        className="bg-indigo-900 hover:bg-indigo-800 shrink-0"
        data-testid="request-elevation-btn"
      >
        Request access
      </Button>
    </div>
  );
};

const NoAgencySelectedBanner = () => (
  <div
    className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-start gap-3"
    data-testid="no-agency-selected-banner"
  >
    <ShieldCheck size={24} className="text-slate-500 shrink-0 mt-0.5" weight="duotone" />
    <div>
      <h3 className="text-sm font-semibold text-slate-900">Select an agency</h3>
      <p className="text-xs text-slate-600 mt-0.5">
        Choose an agency from the switcher in the top bar to view its dashboard.
      </p>
    </div>
  </div>
);

const DashboardPage = () => {
  const { user } = useAuth();
  const { isAzaiStaff, azaiRole, currentAgency } = useAgency();
  const navigate = useNavigate();
  const [showTrends, setShowTrends] = useState(false);

  const {
    stats,
    recentClaims,
    loading,
    activeFilter,
    setActiveFilter,
    handleStatCardClick,
    getFilteredClaims,
    needsElevation,
  } = useDashboardData();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const showCrossAgency =
    isAzaiStaff && (azaiRole === 'super_admin' || azaiRole === 'billing_manager');

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {currentAgency?.status === 'pending_approval' && <PendingApprovalBanner />}
      {isAzaiStaff && !currentAgency && <NoAgencySelectedBanner />}
      {isAzaiStaff && currentAgency && needsElevation && (
        <ElevationRequiredBanner agency={currentAgency} />
      )}
      {isAzaiStaff && (needsElevation || !currentAgency) && (
        <PlatformSummaryWidget />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1
              className="text-3xl font-bold text-slate-900"
              style={{ fontFamily: 'Manrope' }}
            >
              Welcome back, {user?.name?.split(' ')[0]}
            </h1>
            <RealTimeIndicator size="small" />
          </div>
          <p className="text-slate-500 mt-1">Here's your billing overview</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowTrends(!showTrends)}
            className="gap-2"
            data-testid="toggle-trends-btn"
          >
            <ChartLineUp size={18} />
            {showTrends ? 'Hide Charts' : 'Show Charts'}
          </Button>
          <Button
            onClick={() => navigate('/claims/new')}
            className="bg-indigo-900 hover:bg-indigo-800"
            data-testid="dashboard-new-claim-btn"
          >
            <FilePlus size={18} className="mr-2" />
            New Claim
          </Button>
        </div>
      </div>

      {/* Skip tenant-data-dependent widgets when the caller doesn't have
          permission to see them. The elevation banner above tells the
          user what to do next. */}
      {!needsElevation && <ActionRequiredInbox />}

      {!needsElevation && <ManualReviewWidget currentUser={user} />}

      {!needsElevation && (
        <>
          <PrimaryStatsGrid stats={stats} onCardClick={handleStatCardClick} />

          <SecondaryStatsGrid
            stats={stats}
            activeFilter={activeFilter}
            onCardClick={handleStatCardClick}
            onPatientsClick={() => navigate('/patients')}
          />

          {activeFilter && (
            <InlineFilteredResults
              activeFilter={activeFilter}
              claims={getFilteredClaims()}
              onClear={() => setActiveFilter(null)}
            />
          )}

          {showTrends && (
            <div className="animate-fade-in" data-testid="trend-charts-section">
              <TrendCharts agencyId={currentAgency?.id} />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <QuickActions />
            <RecentClaimsCard recentClaims={recentClaims} />
          </div>
        </>
      )}

      {showCrossAgency && <CrossAgencyDashboard />}
    </div>
  );
};

export default DashboardPage;
